import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';
import { concat } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-reimagined/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import StatusBadge from 'tio-reimagined/components/status-badge';
import Timeline from 'tio-reimagined/components/timeline';
import ActionableCard from 'tio-reimagined/components/actionable-card';
import { eq } from 'tio-ui/utilities';
import {
  PhCompass,
  PhLightbulb,
  PhPath,
  PhRobot,
  PhStar,
  PhTrendUp,
  PhFire,
  PhSparkle,
  PhArrowRight,
  PhCheckCircle,
  PhClock,
  PhBriefcase,
  PhCalendarBlank,
  PhCode,
  PhPiggyBank,
  PhTrophy,
  PhBank,
  PhGraduationCap,
} from 'ember-phosphor-icons';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';
import PageHero from 'tio-reimagined/components/page-hero';
import NavCard from 'tio-reimagined/components/nav-card';
import type {
  EmployeeCareerProfile,
  CareerPlan,
  Recommendation,
  CareerSummary,
  Milestone,
} from 'tio-employee/services/career-pathways-api';

interface DashboardModel {
  employee: EmployeeCareerProfile;
  careerPlan: CareerPlan;
  recommendations: Recommendation[];
  summary: CareerSummary;
}

interface CareerPathwaysDashboardSignature {
  Args: {
    model: DashboardModel;
  };
}

interface DashboardTimelineItem {
  id: string;
  status: 'completed' | 'in-progress' | 'planned';
  title: string;
  description: string;
  progress?: number;
  targetDate?: string;
}

export default class CareerPathwaysDashboard extends Component<CareerPathwaysDashboardSignature> {
  get demoClient() {
    return getCurrentDemoClientData();
  }

  get companyName() {
    return this.demoClient.client.name;
  }

  get companyIndustry() {
    return this.demoClient.client.industry;
  }

  get highDemandRoles() {
    const clientId = this.demoClient.client.id;

    if (clientId === 'starbucks') {
      return [
        { title: 'Shift Supervisor', openings: 42, salary: '$42-56k' },
        { title: 'Store Manager', openings: 18, salary: '$65-85k' },
        { title: 'Retail Operations Lead', openings: 12, salary: '$55-75k' },
      ];
    }

    if (clientId === 'ups') {
      return [
        { title: 'Hub Supervisor', openings: 28, salary: '$62-84k' },
        { title: 'Operations Manager', openings: 10, salary: '$90-120k' },
        { title: 'Feeder Driver (CDL-A)', openings: 35, salary: '$78-105k' },
      ];
    }

    return [
      { title: 'Charge Nurse', openings: 15, salary: '$72-95k' },
      { title: 'Nurse Manager', openings: 8, salary: '$85-120k' },
      { title: 'Clinical Informatics', openings: 5, salary: '$75-110k' },
    ];
  }

  get successStoryPreview() {
    const clientId = this.demoClient.client.id;

    if (clientId === 'starbucks') {
      return {
        initials: 'AK',
        name: 'Alex Kim',
        journey: 'Barista → Store Manager',
        quote: 'The store manager development track gave me a clear roadmap to grow my career.',
      };
    }

    if (clientId === 'ups') {
      return {
        initials: 'CJ',
        name: 'Carlos Johnson',
        journey: 'Package Handler → Hub Supervisor',
        quote: 'The leadership program helped me lead a peak season shift with confidence.',
      };
    }

    return {
      initials: 'MR',
      name: 'Maria R.',
      journey: 'CNA → Registered Nurse',
      quote:
        "Career Pathways showed me the RN bridge program was covered by TA. Now I'm mentoring new CNAs!",
    };
  }

  get employee() {
    return this.args.model.employee;
  }

  get careerPlan() {
    return this.args.model.careerPlan;
  }

  get recommendations() {
    return this.args.model.recommendations;
  }

  get summary() {
    return this.args.model.summary;
  }

  get skillsGapTitle() {
    return `Skills needed for ${this.careerPlan.targetRoleTitle || 'your next role'}:`;
  }

  get skillsGapSummary() {
    const clientId = this.demoClient.client.id;

    if (clientId === 'starbucks') {
      return [
        { name: 'Leadership', status: 'Learning', progress: 60, intent: 'warning' },
        { name: 'Customer Experience', status: 'Expert', progress: 90, intent: 'success' },
        { name: 'Labor Planning', status: 'Gap', progress: 25, intent: 'danger' },
        { name: 'Inventory Management', status: 'Learning', progress: 55, intent: 'warning' },
      ];
    }

    if (clientId === 'ups') {
      return [
        { name: 'Leadership', status: 'Learning', progress: 62, intent: 'warning' },
        { name: 'Safety Compliance', status: 'Strong', progress: 82, intent: 'success' },
        { name: 'Staff Scheduling', status: 'Gap', progress: 20, intent: 'danger' },
        { name: 'Process Improvement', status: 'Learning', progress: 48, intent: 'warning' },
      ];
    }

    return [
      { name: 'Leadership', status: 'Learning', progress: 65, intent: 'warning' },
      { name: 'Patient Care', status: 'Expert', progress: 95, intent: 'success' },
      { name: 'Budget Management', status: 'Gap', progress: 20, intent: 'danger' },
      { name: 'Epic EMR', status: 'Learning', progress: 50, intent: 'warning' },
    ];
  }

  get progressPercent() {
    return Math.round(this.careerPlan.progress);
  }

  get progressStyle() {
    return htmlSafe(`width: ${this.progressPercent}%`);
  }

  get milestones(): Milestone[] {
    return this.careerPlan.milestones ?? [];
  }

  get completedMilestone() {
    return this.milestones.find((milestone) => milestone.status === 'completed');
  }

  get inProgressMilestone() {
    return this.milestones.find((milestone) => milestone.status === 'in-progress');
  }

  get plannedMilestone() {
    return this.milestones.find((milestone) => milestone.status === 'planned');
  }

  get dashboardTimelineItems(): DashboardTimelineItem[] {
    const items: DashboardTimelineItem[] = [];

    if (this.completedMilestone) {
      items.push({
        id: this.completedMilestone.id,
        status: 'completed',
        title: this.completedMilestone.title,
        description: this.completedMilestone.description,
        targetDate: this.completedMilestone.completedDate,
      });
    }

    if (this.inProgressMilestone) {
      items.push({
        id: this.inProgressMilestone.id,
        status: 'in-progress',
        title: this.inProgressMilestone.title,
        description: this.inProgressMilestone.description,
        progress: this.inProgressMilestone.progress,
        targetDate: this.inProgressMilestone.targetDate,
      });
    }

    if (this.plannedMilestone) {
      items.push({
        id: this.plannedMilestone.id,
        status: 'planned',
        title: this.plannedMilestone.title,
        description: this.plannedMilestone.description,
        targetDate: this.plannedMilestone.targetDate,
      });
    }

    return items;
  }

  get inProgressStyle() {
    if (!this.inProgressMilestone?.progress) {
      return htmlSafe('width: 0%');
    }

    return htmlSafe(`width: ${this.inProgressMilestone.progress}%`);
  }

  formatDate = (dateValue?: string) => {
    if (!dateValue) return 'TBD';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      year: 'numeric',
    }).format(new Date(dateValue));
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{! Welcome Banner }}
      <PageHero @gradient="brand" @rounded="xl">
        <:default>
          <div class="tio-row-between">
            <div>
              <Heading @level={{1}} class="tio-fg-inverse mb-2">Welcome back,
                {{this.employee.firstName}}!</Heading>
              <Text @size="lg" class="text-violet-100">
                You're
                {{this.progressPercent}}% toward your goal of becoming a
                {{if this.employee.targetRole this.employee.targetRole.title "your next role"}}
              </Text>
              <div class="mt-4 tio-row-loose">
                <div class="tio-row-tight">
                  <PhCheckCircle @size={{20}} />
                  <span>{{this.summary.skillsMastered}} skills mastered</span>
                </div>
                <div class="tio-row-tight">
                  <PhClock @size={{20}} />
                  <span>{{this.summary.coursesInProgress}} courses in progress</span>
                </div>
              </div>
            </div>
            <div class="text-right">
              <div class="text-6xl font-bold">{{this.progressPercent}}%</div>
              <Text class="text-violet-100">Goal Progress</Text>
            </div>
          </div>

          {{! Progress Bar }}
          <div class="mt-6">
            <div class="h-3 bg-violet-400/30 rounded-full overflow-hidden">
              <div
                class="h-full tio-bg-surface rounded-full transition-all"
                style={{this.progressStyle}}
              ></div>
            </div>
          </div>
        </:default>
      </PageHero>

      {{! Quick Actions Grid }}
      <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <NavCard
          @route="authenticated.reimagined.explore"
          @icon={{PhCompass}}
          @title="Explore Careers"
          @subtitle="150+ career paths"
          @intent="brand"
        />

        <NavCard
          @route="authenticated.reimagined.skills"
          @icon={{PhLightbulb}}
          @title="My Skills"
          @subtitle="{{this.summary.skillsTracked}} skills tracked"
          @intent="warning"
        />

        <NavCard
          @route="authenticated.reimagined.loans"
          @icon={{PhBank}}
          @title="My Loans"
          @subtitle="Debt dashboard"
          @intent="info"
        />

        <NavCard
          @route="authenticated.reimagined.benefits"
          @icon={{PhGraduationCap}}
          @title="Benefits"
          @subtitle="Education hub"
          @intent="danger"
        />

        <NavCard
          @route="authenticated.reimagined.advisor"
          @icon={{PhRobot}}
          @title="AI Advisor"
          @subtitle="Get guidance"
          @intent="brand"
        />

        <NavCard
          @route="authenticated.reimagined.finance"
          @icon={{PhPiggyBank}}
          @title="Finance"
          @subtitle="Section 127"
          @intent="success"
        />

        <NavCard
          @route="authenticated.reimagined.score"
          @icon={{PhTrophy}}
          @title="Career Score"
          @subtitle="Track progress"
          @intent="info"
        />
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {{! Main Content Column }}
        <div class="lg:col-span-2 space-y-6">
          {{! Current Career Plan }}
          <Section class="tio-bg-surface rounded-lg shadow-sm dark:shadow-gray-900/50">
            <:header>
              <div class="tio-row-between">
                <div class="tio-row-tight">
                  <PhPath @size={{20}} class="tio-fg-brand" />
                  <span class="dark:text-white">Your Career Plan</span>
                </div>
                <LinkTo @route="authenticated.reimagined.plan">
                  <Button @appearance="outlined" @intent="primary" @size="sm">
                    View Full Plan
                  </Button>
                </LinkTo>
              </div>
            </:header>
            <:body>
              <div class="space-y-4">
                {{! Current Role }}
                <div class="tio-row-loose p-4 tio-bg-sunken dark:bg-gray-700/50 rounded-lg">
                  <div class="p-2 bg-gray-200 dark:bg-gray-600 rounded-full">
                    <PhBriefcase @size={{24}} class="tio-fg-secondary" />
                  </div>
                  <div class="flex-1">
                    <Text @variant="muted" @size="sm">Current Role</Text>
                    <div
                      class="font-semibold tio-fg-primary"
                    >{{this.employee.currentRole.title}}</div>
                  </div>
                  <Chip @intent="default">Current</Chip>
                </div>

                {{! Progress Steps }}
                <Timeline
                  @items={{this.dashboardTimelineItems}}
                  @lineClass="bg-violet-200 dark:bg-violet-800"
                >
                  <:node as |item|>
                    {{#if (eq item.status "completed")}}
                      <div
                        class="w-6 h-6 rounded-full tio-bg-success ring-4 ring-white tio-row-center"
                      >
                        <PhCheckCircle @size={{14}} class="tio-fg-inverse" />
                      </div>
                    {{else if (eq item.status "in-progress")}}
                      <div
                        class="w-6 h-6 rounded-full tio-bg-brand-accent ring-4 ring-white animate-pulse tio-row-center"
                      >
                        <PhClock @size={{14}} class="tio-fg-inverse" />
                      </div>
                    {{else}}
                      <div
                        class="w-6 h-6 rounded-full bg-gray-400 ring-4 ring-white tio-row-center"
                      >
                        <PhCalendarBlank @size={{14}} class="tio-fg-inverse" />
                      </div>
                    {{/if}}
                  </:node>
                  <:content as |item|>
                    <div
                      class="flex-1 min-w-0 p-3 rounded-lg border
                        {{if
                          (eq item.status 'completed')
                          'tio-bg-success-soft tio-border-success'
                          (if
                            (eq item.status 'in-progress')
                            'tio-bg-brand-wash tio-border-brand-soft'
                            'tio-bg-sunken dark:bg-gray-700/50 tio-border-default'
                          )
                        }}"
                    >
                      <div class="tio-row-start-between flex-wrap gap-2">
                        <span
                          class="font-medium break-words
                            {{if
                              (eq item.status 'completed')
                              'tio-fg-success-h'
                              (if (eq item.status 'in-progress') 'tio-fg-brand' 'tio-fg-primary')
                            }}"
                        >
                          {{item.title}}
                        </span>
                        <StatusBadge
                          @status={{item.status}}
                          @label={{if
                            (eq item.status "completed")
                            "Completed"
                            (if (eq item.status "in-progress") "In Progress" "Upcoming")
                          }}
                        />
                      </div>
                      <Text
                        @variant="muted"
                        @size="sm"
                        class="break-words"
                      >{{item.description}}</Text>
                      {{#if (eq item.status "in-progress")}}
                        <div class="mt-2 h-2 tio-bg-brand-soft rounded-full overflow-hidden">
                          <div
                            class="h-full tio-bg-brand-accent rounded-full"
                            style={{this.inProgressStyle}}
                          ></div>
                        </div>
                      {{/if}}
                      {{#if item.targetDate}}
                        <Text @variant="muted" @size="sm" class="mt-2">
                          {{if (eq item.status "completed") "Completed" "Target"}}:
                          {{this.formatDate item.targetDate}}
                        </Text>
                      {{/if}}
                    </div>
                  </:content>
                </Timeline>
                {{! Goal }}
                <div class="tio-flex">
                  <div class="relative shrink-0 z-10">
                    <div
                      class="w-6 h-6 rounded-full tio-gradient-warning ring-4 ring-white tio-row-center"
                    >
                      <PhStar @size={{12}} class="tio-fg-inverse" />
                    </div>
                  </div>
                  <div
                    class="flex-1 min-w-0 p-4 tio-gradient-warning-soft rounded-lg border tio-border-warning"
                  >
                    <div class="tio-row-start-between flex-wrap gap-2">
                      <span class="font-semibold tio-fg-warning-h break-words">
                        {{if
                          this.employee.targetRole
                          this.employee.targetRole.title
                          "Set Your Goal"
                        }}
                      </span>
                      <Chip @intent="warning">Goal</Chip>
                    </div>
                    <Text @variant="muted" @size="sm">
                      Est. timeline:
                      {{this.careerPlan.estimatedTimeMonths}}
                      months
                    </Text>
                  </div>
                </div>
              </div>
            </:body>
          </Section>

          {{! Personalized Recommendations }}
          <Section class="tio-bg-surface rounded-lg shadow-sm dark:shadow-gray-900/50">
            <:header>
              <div class="tio-row-between">
                <div class="tio-row-tight">
                  <PhSparkle @size={{20}} class="tio-fg-brand" />
                  <span class="dark:text-white">Recommended For You</span>
                </div>
                <LinkTo @route="authenticated.reimagined.recommendations">
                  <Button @appearance="outlined" @intent="primary" @size="sm">
                    See All
                  </Button>
                </LinkTo>
              </div>
            </:header>
            <:body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                {{#each this.recommendations as |rec|}}
                  <ActionableCard
                    @cardClass="bg-white dark:bg-gray-700/30 rounded-lg border tio-border-default shadow-sm overflow-hidden hover:border-violet-300 dark:hover:border-violet-600 hover:shadow-md transition-all cursor-pointer"
                  >
                    <:header>
                      <div class="tio-row-start-between mb-3">
                        <Chip @intent="success">{{rec.matchScore}}% Match</Chip>
                        {{#if rec.taEligible}}
                          <Chip @intent="primary">TA Eligible</Chip>
                        {{/if}}
                      </div>
                      <Heading @level={{4}} class="mb-2 dark:text-white">
                        {{rec.title}}
                      </Heading>
                    </:header>
                    <:metadata>
                      <Text @variant="muted" @size="sm" class="mb-3">
                        {{rec.provider}}
                        •
                        {{rec.duration}}
                      </Text>
                      <div class="tio-row-tight text-sm tio-fg-muted">
                        <PhTrendUp @size={{16}} class="tio-fg-success" />
                        <span>{{rec.skillsGained.length}} skills gained</span>
                      </div>
                    </:metadata>
                  </ActionableCard>
                {{/each}}
              </div>
            </:body>
          </Section>
        </div>

        {{! Sidebar }}
        <div class="space-y-6">
          {{! Company Spotlight }}
          <Section class="tio-bg-surface rounded-lg shadow-sm dark:shadow-gray-900/50">
            <:header>
              <div class="tio-row-tight">
                <PhFire @size={{20}} class="tio-fg-warning" />
                <span class="dark:text-white">High Demand at {{this.companyName}}</span>
              </div>
            </:header>
            <:body>
              <Text @variant="muted" @size="sm" class="mb-4">
                Your company is actively hiring for these roles:
              </Text>
              <div class="space-y-3">
                {{#each this.highDemandRoles as |role|}}
                  <div
                    class="tio-row-between p-3 tio-bg-warning-soft rounded-lg border tio-border-warning"
                  >
                    <div>
                      <div class="font-medium tio-fg-primary">{{role.title}}</div>
                      <Text @variant="muted" @size="sm">
                        {{role.openings}}
                        openings •
                        {{role.salary}}
                      </Text>
                    </div>
                    <PhArrowRight @size={{18}} class="tio-fg-warning" />
                  </div>
                {{/each}}
              </div>
              <Button @intent="primary" @appearance="outlined" class="w-full mt-4">
                View Bridge Paths
              </Button>
            </:body>
          </Section>

          {{! Success Story Preview }}
          <Section class="tio-bg-surface rounded-lg shadow-sm dark:shadow-gray-900/50">
            <:header>
              <div class="tio-row-tight">
                <PhStar @size={{20}} class="tio-fg-warning" />
                <span class="dark:text-white">Success Story</span>
              </div>
            </:header>
            <:body>
              <div class="text-center">
                {{! Placeholder for profile image }}
                <div
                  class="w-16 h-16 mx-auto mb-3 rounded-full tio-gradient-brand tio-row-center tio-fg-inverse text-xl font-bold"
                >
                  {{this.successStoryPreview.initials}}
                </div>
                <Heading @level={{4}}>
                  {{this.successStoryPreview.name}}
                </Heading>
                <Text @variant="muted" @size="sm" class="mb-3">
                  {{this.successStoryPreview.journey}}
                </Text>
                <blockquote
                  class="text-sm italic tio-fg-secondary border-l-4 tio-border-brand pl-3"
                >
                  "{{this.successStoryPreview.quote}}"
                </blockquote>
                <LinkTo @route="authenticated.reimagined.success-stories">
                  <Button @intent="primary" @appearance="outlined" @size="sm" class="mt-4">
                    Read More Stories
                  </Button>
                </LinkTo>
              </div>
            </:body>
          </Section>

          {{! Skills Gap Summary }}
          <Section class="tio-bg-surface rounded-lg shadow-sm dark:shadow-gray-900/50">
            <:header>
              <div class="tio-row-tight">
                <PhLightbulb @size={{20}} class="tio-fg-warning" />
                <span class="dark:text-white">Skills Gap</span>
              </div>
            </:header>
            <:body>
              <Text @variant="muted" @size="sm" class="mb-4">
                {{this.skillsGapTitle}}
              </Text>
              <div class="space-y-3">
                {{#each this.skillsGapSummary as |gap|}}
                  <div>
                    <div class="tio-flex-between text-sm mb-1">
                      <span class="dark:text-gray-200">{{gap.name}}</span>
                      <span
                        class="{{if
                            (eq gap.intent 'success')
                            'tio-fg-success'
                            (if (eq gap.intent 'danger') 'tio-fg-danger' 'tio-fg-warning')
                          }}"
                      >{{gap.status}}</span>
                    </div>
                    <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        class="h-full rounded-full
                          {{if
                            (eq gap.intent 'success')
                            'tio-bg-success'
                            (if (eq gap.intent 'danger') 'tio-bg-danger' 'tio-bg-warning')
                          }}"
                        style={{htmlSafe (concat "width: " gap.progress "%")}}
                      ></div>
                    </div>
                  </div>
                {{/each}}
              </div>
              <LinkTo @route="authenticated.reimagined.skills">
                <Button @intent="primary" @appearance="outlined" class="w-full mt-4">
                  View Full Skills Profile
                </Button>
              </LinkTo>
            </:body>
          </Section>
        </div>
      </div>

      {{! Developer Tools (Demo Only) }}
      <div class="mt-8 border-t tio-border-default pt-6">
        <div class="tio-row-between">
          <div class="tio-row-tight tio-fg-disabled">
            <PhCode @size={{18}} />
            <Text @variant="muted" @size="sm">Developer Tools (Demo Only)</Text>
          </div>
          <LinkTo
            @route="authenticated.reimagined.components"
            class="text-sm tio-fg-muted hover:tio-fg-brand transition-colors"
          >
            View UI Component Patterns →
          </LinkTo>
        </div>
      </div>
    </VStack>
  </template>
}
